/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { chunk, identity, isString, property } from 'lodash';
import { CheckboxTreeItemComponent } from './checkbox-tree-item.component';
import { CheckboxTreeNode } from './checkbox-tree-node';
import { forEachNode, normalizeNode } from './checkbox-tree-util';

import { CheckboxTreeService } from './checkbox-tree.service';

@Component({
  selector: 'gh-checkbox-tree',
  template: require('./checkbox-tree.component.html'),
  styles: [require('./checkbox-tree.component.scss')],
  providers: [CheckboxTreeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxTreeComponent<T> {
  @Input() disabled: boolean;

  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() change = this.checkboxTreeService.change;

  @ViewChildren(CheckboxTreeItemComponent) children: QueryList<CheckboxTreeItemComponent<T>>;

  columns: CheckboxTreeNode<T>[][];

  private _nodes: CheckboxTreeNode<T>[];
  private _trackBy: TransformFn<T, any> = identity;

  @Input()
  set nodes(nodes: CheckboxTreeNode<T>[]) {
    this._nodes = nodes;
    forEachNode(nodes, normalizeNode);
    this.updateColumnLayout();
  }

  get nodes(): CheckboxTreeNode<T>[] {
    return this._nodes;
  }

  @Input()
  set trackBy(trackBy: any) {
    this._trackBy = isString(trackBy) ? property(trackBy) : trackBy;
  }

  get trackBy(): any {
    return this._trackBy;
  }

  constructor(private checkboxTreeService: CheckboxTreeService) {
    this.trackByNode = this.trackByNode.bind(this);
  }

  expandAll(): void {
    this.children.forEach((child) => child.foldAll(true));
  }

  collapseAll(): void {
    this.children.forEach((child) => child.foldAll(false));
  }

  trackByColumn(index: number): any {
    return index;
  }

  trackByNode(index: number, item: CheckboxTreeNode<T>): any {
    return this._trackBy(item.value);
  }

  private updateColumnLayout(): void {
    const nodes = this._nodes;
    this.columns = chunk(nodes, Math.ceil(nodes.length / 3)); // tslint:disable-line:no-magic-numbers
  }
}
